// src/utils/aiFunctions.js
// Funciones que el agente IA puede ejecutar contra Supabase
import supabase from "../supabaseClient";

// ─── Helpers ───
const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;
const sinTildes = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
// Neutraliza caracteres con significado en la sintaxis .or() de PostgREST
// (coma y paréntesis), evitando que texto del usuario inyecte condiciones en el filtro.
const escaparOr = (t) => String(t ?? "").replace(/[(),]/g, " ").trim();
const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fechaLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

/**
 * Búsqueda inteligente en 3 niveles:
 * 1. Frase exacta (ilike %texto%)
 * 2. Todas las palabras AND (cada palabra debe estar)
 * 3. Cualquier palabra OR (coincidencias parciales como sugerencias)
 */
async function busquedaInteligente(tabla, campo, texto, selectCols, limite = 10) {
  const textoSeguro = escaparOr(texto);
  const textoNormal = sinTildes(textoSeguro);

  // Nivel 1: frase exacta
  let { data } = await supabase.from(tabla).select(selectCols)
    .or(`${campo}.ilike.%${textoSeguro}%,${campo}.ilike.%${textoNormal}%`)
    .limit(limite);
  if (data && data.length > 0) return data;

  // Nivel 2: todas las palabras AND
  const palabras = sinTildes(textoSeguro).toLowerCase().split(/\s+/).filter(p => p.length > 1);
  if (palabras.length > 1) {
    let query = supabase.from(tabla).select(selectCols);
    for (const p of palabras) { query = query.ilike(campo, `%${p}%`); }
    const res = await query.limit(limite);
    if (res.data && res.data.length > 0) return res.data;
  }

  // Nivel 3: cualquier palabra OR — coincidencias parciales
  if (palabras.length > 0) {
    const orFilter = palabras.map(p => `${campo}.ilike.%${p}%`).join(",");
    const res = await supabase.from(tabla).select(selectCols).or(orFilter).limit(limite);
    if (res.data && res.data.length > 0) return res.data;
  }

  return [];
}

/**
 * Cuando no encontramos un artículo, buscamos parecidos en el inventario
 * para que Renty pueda preguntar "¿te referías a...?" en vez de dejar al
 * usuario adivinando cómo se llama exactamente.
 * Reutiliza busquedaInteligente, que ya tolera tildes y palabras incompletas.
 */
async function articulosParecidos(texto, limite = 5) {
  const encontrados = await busquedaInteligente("productos", "nombre", texto, "nombre, stock", limite);
  return encontrados || [];
}

const listaParecidos = (arr) => arr.map((p) => `• ${p.nombre}`).join("\n");

// ═══════════════════════════════════════════════════════════
// 1. VERIFICAR DISPONIBILIDAD
// ═══════════════════════════════════════════════════════════
export async function verificar_disponibilidad({ articulo, fecha }) {
  const fechaBuscar = /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? fecha : hoyISO();
  const productos = await busquedaInteligente("productos", "nombre", articulo, "id, nombre, stock, precio, tipo", 5);
  if (productos.length === 0) return `No encontré artículos que coincidan con "${articulo}". ¿Quizás se llama diferente?`;
  const resultados = [];
  for (const prod of productos) {
    const { data: ordenes } = await supabase.from("ordenes_pedido").select("productos, fecha_entrega, fecha_devolucion").or(`fecha_evento.eq.${fechaBuscar},and(fecha_entrega.lte.${fechaBuscar},fecha_devolucion.gte.${fechaBuscar})`);
    let comprometidos = 0;
    for (const orden of ordenes || []) {
      const walk = (list, factor = 1) => { for (const it of list) { if (it.es_grupo && Array.isArray(it.productos)) { walk(it.productos, factor * (Number(it.cantidad) || 1)); } else if ((it.id === prod.id || it.producto_id === prod.id) && !it.es_servicio) { comprometidos += (Number(it.cantidad) || 0) * factor; } } };
      walk(orden.productos || []);
    }
    const stockTotal = Number(prod.stock || 0);
    resultados.push({ nombre: prod.nombre, tipo: prod.tipo || "articulo", stock_total: stockTotal, comprometidos, disponible: Math.max(0, stockTotal - comprometidos), precio_alquiler: Number(prod.precio || 0) });
  }
  return JSON.stringify(resultados);
}

// ═══════════════════════════════════════════════════════════
// 2. BUSCAR CLIENTE
// ═══════════════════════════════════════════════════════════
export async function buscar_cliente({ texto }) {
  const textoSeguro = escaparOr(texto);
  const textoNormal = sinTildes(textoSeguro);
  // Primero: búsqueda multi-campo
  let { data } = await supabase.from("clientes").select("id, nombre, telefono, email, identificacion, direccion")
    .or(`nombre.ilike.%${textoSeguro}%,nombre.ilike.%${textoNormal}%,telefono.ilike.%${textoSeguro}%,email.ilike.%${textoSeguro}%,identificacion.ilike.%${textoSeguro}%`)
    .limit(5);
  // Fallback: búsqueda inteligente por nombre
  if (!data || data.length === 0) {
    data = await busquedaInteligente("clientes", "nombre", texto, "id, nombre, telefono, email, identificacion, direccion", 5);
  }
  if (!data || data.length === 0) return `No encontré clientes que coincidan con "${texto}". ¿Puede estar registrado con otro nombre?`;
  return JSON.stringify(data.map((c) => ({ id: c.id, nombre: c.nombre, telefono: c.telefono || "—", email: c.email || "—", identificacion: c.identificacion || "—", direccion: c.direccion || "—" })));
}

// ═══════════════════════════════════════════════════════════
// 3. BUSCAR PRODUCTO
// ═══════════════════════════════════════════════════════════
export async function buscar_producto({ nombre }) {
  const data = await busquedaInteligente("productos", "nombre", nombre, "nombre, precio, stock, tipo, costo, categoria, valor_adquisicion", 10);
  if (data.length === 0) return `No encontré productos que coincidan con "${nombre}". ¿Quizás se llama diferente? Puedo mostrarte todo el inventario si quieres.`;
  return JSON.stringify(data.map((p) => ({ nombre: p.nombre, tipo: p.tipo || "articulo", precio_alquiler: money(p.precio), stock: p.tipo === "servicio" ? "Ilimitado" : Number(p.stock || 0), costo_interno: Number(p.costo || 0) > 0 ? money(p.costo) : "—", valor_adquisicion: Number(p.valor_adquisicion || 0) > 0 ? money(p.valor_adquisicion) : "—", categoria: p.categoria || "—" })));
}

// ═══════════════════════════════════════════════════════════
// 4. CONSULTAR PEDIDOS
// ═══════════════════════════════════════════════════════════
export async function consultar_pedidos({ fecha, fecha_desde, fecha_hasta, cliente }) {
  let query = supabase.from("ordenes_pedido").select("id, numero, fecha_evento, total_neto, estado, tipo_evento, lugar_evento, clientes(nombre)").order("fecha_evento", { ascending: true }).limit(15);
  if (fecha) query = query.eq("fecha_evento", fecha);
  else if (fecha_desde && fecha_hasta) query = query.gte("fecha_evento", fecha_desde).lte("fecha_evento", fecha_hasta);
  else query = query.gte("fecha_evento", hoyISO()); // Sin filtro = solo pedidos futuros
  const { data, error } = await query;
  if (error || !data || data.length === 0) return "No se encontraron pedidos para los criterios indicados.";
  let resultado = data;
  if (cliente) { const cn = sinTildes(cliente.toLowerCase()); resultado = resultado.filter((p) => { const nom = (p.clientes?.nombre || "").toLowerCase(); return nom.includes(cliente.toLowerCase()) || sinTildes(nom).includes(cn); }); }
  if (resultado.length === 0) return `No se encontraron pedidos del cliente "${cliente}".`;
  return JSON.stringify(resultado.map((p) => ({ _id: p.id, _tipo: "pedido", numero: p.numero || "—", cliente: p.clientes?.nombre || "—", fecha_evento: p.fecha_evento, total: money(p.total_neto), estado: p.estado || "pendiente", tipo_evento: p.tipo_evento || "", lugar_evento: p.lugar_evento || "" })));
}

// ═══════════════════════════════════════════════════════════
// 5. CONSULTAR AGENDA (periodo) — ahora incluye citas
// ═══════════════════════════════════════════════════════════
export async function consultar_agenda({ periodo }) {
  const hoy = new Date(); let desde = hoyISO(); let hasta = hoyISO();
  if (periodo === "manana") { const m = new Date(hoy); m.setDate(m.getDate() + 1); desde = hasta = fechaLocal(m); }
  else if (periodo === "semana" || periodo === "proximos_7_dias") { const f = new Date(hoy); f.setDate(f.getDate() + 7); hasta = fechaLocal(f); }
  else if (periodo === "proximos_30_dias") { const f = new Date(hoy); f.setDate(f.getDate() + 30); hasta = fechaLocal(f); }

  const [{ data: pedidos }, { data: citas }] = await Promise.all([
    supabase.from("ordenes_pedido").select("id, numero, fecha_evento, fecha_entrega, total_neto, clientes(nombre)").gte("fecha_evento", desde).lte("fecha_evento", hasta).order("fecha_evento", { ascending: true }).limit(20),
    supabase.from("citas").select("id, fecha, hora, motivo, estado, clientes(nombre)").gte("fecha", desde).lte("fecha", hasta).in("estado", ["programada", "confirmada"]).order("fecha").order("hora").limit(20),
  ]);

  const hayPedidos = pedidos && pedidos.length > 0;
  const hayCitas = citas && citas.length > 0;
  const label = periodo === "hoy" ? "hoy" : periodo === "manana" ? "mañana" : `los próximos ${periodo === "semana" ? "7" : "30"} días`;

  if (!hayPedidos && !hayCitas) return `No hay eventos ni citas para ${label}.`;

  const resultado = {
    pedidos: (pedidos || []).map((p) => ({ _id: p.id, _tipo: "pedido", numero: p.numero || "—", cliente: p.clientes?.nombre || "—", fecha_evento: p.fecha_evento, fecha_entrega: p.fecha_entrega || "—", total: money(p.total_neto) })),
    citas: (citas || []).map((c) => ({ id: c.id, fecha: c.fecha, hora: (c.hora || "").slice(0, 5), cliente: c.clientes?.nombre || "—", motivo: c.motivo || "—", estado: c.estado })),
    total_pedidos: (pedidos || []).length,
    total_citas: (citas || []).length,
  };
  return JSON.stringify(resultado);
}

// ═══════════════════════════════════════════════════════════
// 5B. CONSULTAR AGENDA POR FECHA — ahora incluye citas
// ═══════════════════════════════════════════════════════════
export async function consultar_agenda_fecha({ fecha }) {
  const fechaBuscar = fecha || hoyISO();
  const [{ data: pedidos }, { data: cotizaciones }, { data: notas }, { data: citas }] = await Promise.all([
    supabase.from("ordenes_pedido").select("id, numero, fecha_evento, fecha_entrega, total_neto, estado, clientes(nombre)").eq("fecha_evento", fechaBuscar),
    supabase.from("cotizaciones").select("id, numero, fecha_evento, total_neto, clientes(nombre)").eq("fecha_evento", fechaBuscar),
    supabase.from("agenda").select("id, titulo, descripcion, fecha").eq("fecha", fechaBuscar).order("created_at", { ascending: true }),
    supabase.from("citas").select("id, fecha, hora, motivo, estado, notas, clientes(nombre, telefono)").eq("fecha", fechaBuscar).order("hora"),
  ]);
  const resultado = {
    fecha: fechaBuscar,
    pedidos: (pedidos || []).map((p) => ({ _id: p.id, _tipo: "pedido", numero: p.numero || "—", cliente: p.clientes?.nombre || "—", fecha_entrega: p.fecha_entrega || "—", total: money(p.total_neto), estado: p.estado || "pendiente" })),
    cotizaciones: (cotizaciones || []).map((c) => ({ _id: c.id, _tipo: "cotizacion", numero: c.numero || "—", cliente: c.clientes?.nombre || "—", total: money(c.total_neto) })),
    notas: (notas || []).map((n) => ({ id: n.id, descripcion: n.descripcion || n.titulo || "—" })),
    citas: (citas || []).map((c) => ({ id: c.id, hora: (c.hora || "").slice(0, 5), cliente: c.clientes?.nombre || "—", telefono: c.clientes?.telefono || "—", motivo: c.motivo || "—", estado: c.estado, notas: c.notas || "" })),
    total_pedidos: (pedidos || []).length, total_cotizaciones: (cotizaciones || []).length, total_notas: (notas || []).length, total_citas: (citas || []).length,
  };
  if (resultado.total_pedidos === 0 && resultado.total_cotizaciones === 0 && resultado.total_notas === 0 && resultado.total_citas === 0) return `No hay nada programado para el ${fechaBuscar}. El día está libre.`;
  return JSON.stringify(resultado);
}

// ═══════════════════════════════════════════════════════════
// 6. RESUMEN FINANCIERO
// ═══════════════════════════════════════════════════════════
export async function resumen_financiero({ mes, anio }) {
  const ahora = new Date(); const m = mes || (ahora.getMonth() + 1); const a = anio || ahora.getFullYear();
  const desde = `${a}-${String(m).padStart(2, "0")}-01`; const ultimoDia = new Date(a, m, 0).getDate(); const hasta = `${a}-${String(m).padStart(2, "0")}-${String(ultimoDia).padStart(2, "0")}`;
  const { data, error } = await supabase.from("movimientos_contables").select("tipo, monto, categoria").gte("fecha", desde).lte("fecha", hasta).neq("estado", "eliminado");
  if (error || !data) return "Error al consultar movimientos contables.";
  const ingresos = data.filter((m) => m.tipo === "ingreso").reduce((s, m) => s + Number(m.monto || 0), 0);
  const gastos = data.filter((m) => m.tipo === "gasto").reduce((s, m) => s + Number(m.monto || 0), 0);
  const ganancia = ingresos - gastos;
  const catGastos = {}; data.filter((m) => m.tipo === "gasto").forEach((m) => { const cat = m.categoria || "Sin categoría"; catGastos[cat] = (catGastos[cat] || 0) + Number(m.monto || 0); });
  const topGastos = Object.entries(catGastos).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return JSON.stringify({ periodo: `${meses[m - 1]} ${a}`, ingresos: money(ingresos), gastos: money(gastos), ganancia: money(ganancia), margen: ingresos > 0 ? `${Math.round((ganancia / ingresos) * 100)}%` : "0%", movimientos_totales: data.length, top_gastos: topGastos.map(([cat, val]) => `${cat}: ${money(val)}`) });
}

// ═══════════════════════════════════════════════════════════
// 7. CONTAR REGISTROS
// ═══════════════════════════════════════════════════════════
export async function contar_registros({ tipo }) {
  const tablas = { clientes: "clientes", productos: "productos", pedidos: "ordenes_pedido", cotizaciones: "cotizaciones", paquetes: "paquetes_eventos", citas: "citas" };
  const tabla = tablas[tipo]; if (!tabla) return `Tipo "${tipo}" no reconocido. Opciones: clientes, productos, pedidos, cotizaciones, paquetes, citas.`;
  const { count, error } = await supabase.from(tabla).select("id", { count: "exact", head: true });
  if (error) return `Error al contar ${tipo}.`;
  return JSON.stringify({
    tipo,
    total: count,
    periodo: "TOTAL HISTÓRICO — todos los registros desde siempre. NO corresponde a un mes ni a ningún período.",
  });
}

// ═══════════════════════════════════════════════════════════
// 8. CONSULTAR COTIZACIONES
// ═══════════════════════════════════════════════════════════
export async function consultar_cotizaciones({ fecha_desde, fecha_hasta, cliente }) {
  let query = supabase.from("cotizaciones").select("id, numero, fecha_evento, total_neto, tipo_evento, lugar_evento, clientes(nombre)").order("fecha_evento", { ascending: false }).limit(15);
  if (fecha_desde && fecha_hasta) query = query.gte("fecha_evento", fecha_desde).lte("fecha_evento", fecha_hasta);
  const { data, error } = await query;
  if (error || !data || data.length === 0) return "No se encontraron cotizaciones.";
  let resultado = data;
  if (cliente) { const cn = sinTildes(cliente.toLowerCase()); resultado = resultado.filter((c) => { const nom = (c.clientes?.nombre || "").toLowerCase(); return nom.includes(cliente.toLowerCase()) || sinTildes(nom).includes(cn); }); }
  if (resultado.length === 0) return `No se encontraron cotizaciones del cliente "${cliente}".`;
  return JSON.stringify(resultado.map((c) => ({ _id: c.id, _tipo: "cotizacion", numero: c.numero || "—", cliente: c.clientes?.nombre || "—", fecha_evento: c.fecha_evento, total: money(c.total_neto), tipo_evento: c.tipo_evento || "", lugar_evento: c.lugar_evento || "" })));
}

// ═══════════════════════════════════════════════════════════
// 9. TRAZABILIDAD DE PRECIO
// ═══════════════════════════════════════════════════════════
export async function trazabilidad_precio({ articulo, cliente }) {
  const clienteData = await busquedaInteligente("clientes", "nombre", cliente, "id, nombre", 3);
  if (clienteData.length === 0) return `No se encontró el cliente "${cliente}".`;
  const clienteEncontrado = clienteData[0];
  const [{ data: pedidos }, { data: cotizaciones }] = await Promise.all([
    supabase.from("ordenes_pedido").select("id, numero, fecha_evento, productos").eq("cliente_id", clienteEncontrado.id).order("fecha_evento", { ascending: false }).limit(50),
    supabase.from("cotizaciones").select("id, numero, fecha_evento, productos").eq("cliente_id", clienteEncontrado.id).order("fecha_evento", { ascending: false }).limit(50),
  ]);
  const todos = [...(pedidos || []), ...(cotizaciones || [])].sort((a, b) => new Date(b.fecha_evento || 0) - new Date(a.fecha_evento || 0));
  const nombreBuscado = sinTildes(articulo.toLowerCase()); const coincidencias = [];
  for (const doc of todos) {
    const esPedido = (pedidos || []).some((p) => p.id === doc.id);
    const walk = (list, factor = 1) => { for (const it of list) { if (it.es_grupo && Array.isArray(it.productos)) { walk(it.productos, factor * (Number(it.cantidad) || 1)); } else { const nomItem = sinTildes((it.nombre || "").toLowerCase()); if (nomItem.includes(nombreBuscado) || nombreBuscado.split(/\s+/).filter(p => p.length > 1).every(p => nomItem.includes(p))) { coincidencias.push({ _id: doc.id, _tipo: esPedido ? "pedido" : "cotizacion", documento: doc.numero || "—", fecha: doc.fecha_evento, articulo: it.nombre, precio_unitario: Number(it.precio || 0), cantidad: (Number(it.cantidad) || 0) * factor }); } } } };
    walk(doc.productos || []);
  }
  if (coincidencias.length === 0) {
    const similares = await articulosParecidos(articulo);
    if (similares.length > 0) {
      return `No encontré "${articulo}" en los documentos de ${clienteEncontrado.nombre}. En el inventario tengo estos parecidos:\n${listaParecidos(similares)}\n\nMuéstrale la lista al usuario y pregúntale a cuál se refería. NO elijas tú.`;
    }
    return `No se encontró "${articulo}" en los documentos de ${clienteEncontrado.nombre}, ni nada parecido en el inventario.`;
  }
  const ultimo = coincidencias[0];
  return JSON.stringify({ cliente: clienteEncontrado.nombre, articulo_buscado: articulo, ultimo_precio: money(ultimo.precio_unitario), ultimo_documento: ultimo.documento, ultima_fecha: ultimo.fecha, ultima_cantidad: ultimo.cantidad, total_veces_alquilado: coincidencias.length, _acciones: coincidencias.slice(0, 3).map((c) => ({ _id: c._id, _tipo: c._tipo, numero: c.documento })), historial: coincidencias.slice(0, 5).map((c) => ({ documento: c.documento, fecha: c.fecha, precio: money(c.precio_unitario), cantidad: c.cantidad })) });
}

// ═══════════════════════════════════════════════════════════
// 10. ÚLTIMO CLIENTE QUE ALQUILÓ UN ARTÍCULO
// ═══════════════════════════════════════════════════════════
export async function ultimo_cliente_articulo({ articulo }) {
  const nombreBuscado = sinTildes(articulo.toLowerCase());
  const palabrasBuscadas = nombreBuscado.split(/\s+/).filter(p => p.length > 1);

  // 📅 Hoy en fecha local (UTC-5). Con toISOString() se adelantaría un día.
  const h = new Date();
  const hoy = `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;

  // "La última vez que alquilé" = el evento PASADO más reciente.
  // Antes se ordenaba por fecha_evento DESC sin filtrar y pasaban dos cosas:
  //   1) Postgres pone los NULL de primeros en DESC → un pedido sin fecha de
  //      evento ganaba siempre (por eso salía el mismo pedido viejo cada vez).
  //   2) Las reservas futuras contaban como "última vez".
  const { data: pedidos } = await supabase
    .from("ordenes_pedido")
    .select("id, numero, fecha_evento, productos, cliente_id, clientes(nombre)")
    .not("fecha_evento", "is", null)
    .lte("fecha_evento", hoy)
    .order("fecha_evento", { ascending: false })
    .limit(200);

  if (!pedidos || pedidos.length === 0) return "No hay pedidos pasados registrados.";
  for (const ped of pedidos) {
    const walk = (list, factor = 1) => { for (const it of list) { if (it.es_grupo && Array.isArray(it.productos)) { const f = walk(it.productos, factor * (Number(it.cantidad) || 1)); if (f) return f; } else { const nomItem = sinTildes((it.nombre || "").toLowerCase()); const coincide = nomItem.includes(nombreBuscado) || (palabrasBuscadas.length > 1 && palabrasBuscadas.every(p => nomItem.includes(p))); if (coincide) return { cliente: ped.clientes?.nombre || "Desconocido", fecha: ped.fecha_evento, documento: ped.numero, articulo: it.nombre, precio: Number(it.precio || 0), cantidad: (Number(it.cantidad) || 0) * factor, _id: ped.id }; } } return null; };
    const encontrado = walk(ped.productos || []);
    if (encontrado) return JSON.stringify({ mensaje: `El último cliente fue ${encontrado.cliente}, en el evento del ${encontrado.fecha}`, cliente: encontrado.cliente, fecha: encontrado.fecha, articulo: encontrado.articulo, precio_unitario: money(encontrado.precio), cantidad: encontrado.cantidad, documento: encontrado.documento, _acciones: [{ _id: encontrado._id, _tipo: "pedido", numero: encontrado.documento }] });
  }
  // No apareció en pedidos pasados. Distinguimos tres casos, porque
  // "no existe" y "existe pero nunca lo has alquilado" son cosas distintas.
  const similares = await articulosParecidos(articulo);

  const exacto = similares.find(
    (p) => sinTildes(p.nombre.toLowerCase()) === nombreBuscado
  );
  if (exacto) {
    return `"${exacto.nombre}" existe en el inventario, pero no aparece en ningún pedido pasado reciente. Puede que solo esté en reservas a futuro.`;
  }

  if (similares.length > 0) {
    return `No encontré "${articulo}" en los pedidos pasados. En el inventario tengo estos parecidos:\n${listaParecidos(similares)}\n\nMuéstrale esta lista al usuario y pregúntale a cuál se refería. NO elijas tú.`;
  }

  return `No encontré "${articulo}" ni en los pedidos pasados ni en el inventario. Puede estar registrado con otro nombre.`;
}

// ═══════════════════════════════════════════════════════════
// 11. CREAR NOTA EN CALENDARIO
// ═══════════════════════════════════════════════════════════
export async function crear_nota({ fecha, descripcion }) {
  if (!descripcion || !descripcion.trim()) return "Necesito una descripción para la nota.";
  const fechaNota = fecha || hoyISO();
  const { error } = await supabase.from("agenda").insert([{ titulo: "Nota", descripcion: descripcion.trim(), fecha: fechaNota, documento: null, tipo: null }]);
  if (error) { console.error("Error creando nota:", error); return "Error al crear la nota en el calendario."; }
  return JSON.stringify({ exito: true, mensaje: `✅ Nota creada para el ${fechaNota}`, fecha: fechaNota, descripcion: descripcion.trim() });
}

// ═══════════════════════════════════════════════════════════
// 12. PAGOS PENDIENTES
// ═══════════════════════════════════════════════════════════
export async function pagos_pendientes({ cliente }) {
  const { data: ordenes, error } = await supabase.from("ordenes_pedido").select("id, numero, fecha_evento, total_neto, abonos, clientes(nombre)").order("fecha_evento", { ascending: false }).limit(200);
  if (error || !ordenes) return "Error al consultar pagos pendientes.";
  let pendientes = ordenes.map((o) => { const total = Number(o.total_neto || 0); const abonado = (o.abonos || []).reduce((s, a) => s + Number(a.valor || a || 0), 0); return { ...o, abonado, saldo: Math.max(0, total - abonado) }; }).filter((o) => o.saldo > 0);
  if (cliente) { const cn = sinTildes(cliente.toLowerCase()); pendientes = pendientes.filter((o) => { const nom = (o.clientes?.nombre || "").toLowerCase(); return nom.includes(cliente.toLowerCase()) || sinTildes(nom).includes(cn); }); }
  if (pendientes.length === 0) return cliente ? `No se encontraron pagos pendientes del cliente "${cliente}".` : "¡Todo al día! No hay pagos pendientes.";
  const totalPendiente = pendientes.reduce((s, o) => s + o.saldo, 0);
  const mostrar = pendientes.slice(0, 15);
  const hayMas = pendientes.length > 15 ? `\n(Mostrando 15 de ${pendientes.length} pendientes)` : "";
  return JSON.stringify({ total_pendiente: money(totalPendiente), cantidad_pedidos: pendientes.length, nota: hayMas, pedidos: mostrar.map((o) => ({ _id: o.id, _tipo: "pedido", numero: o.numero || "—", cliente: o.clientes?.nombre || "—", fecha_evento: o.fecha_evento, total: money(o.total_neto), abonado: money(o.abonado), saldo: money(o.saldo) })) });
}

// ═══════════════════════════════════════════════════════════
// 13. CREAR CLIENTE
// ═══════════════════════════════════════════════════════════
export async function crear_cliente({ nombre, telefono, identificacion, email, direccion }) {
  if (!nombre || !nombre.trim()) return "Necesito al menos el nombre del cliente para crearlo.";
  const soloDigitos = (telefono || "").replace(/\D/g, "");
  if (soloDigitos.length < 7) return `Para registrar a ${nombre.trim()} necesito sus datos: teléfono (obligatorio), y si tienes: cédula, email o dirección. Dime los datos y me encargo.`;
  const nombreSeguro = escaparOr(nombre);
  const nombreNormal = sinTildes(nombreSeguro);
  const { data: existentes } = await supabase.from("clientes").select("id, nombre").or(`nombre.ilike.%${nombreSeguro}%,nombre.ilike.%${nombreNormal}%`).limit(3);
  if (existentes && existentes.length > 0) return JSON.stringify({ exito: false, mensaje: `Ya existe un cliente similar: ${existentes.map((c) => c.nombre).join(", ")}. ¿Deseas crearlo de todas formas?`, clientes_similares: existentes.map((c) => c.nombre) });
  const { data, error } = await supabase.from("clientes").insert([{ nombre: nombre.trim(), telefono: telefono || null, identificacion: identificacion || null, email: email || null, direccion: direccion || null }]).select("id, nombre").single();
  if (error) { console.error("Error creando cliente:", error); return "Error al crear el cliente."; }
  return JSON.stringify({ exito: true, mensaje: `✅ Cliente "${data.nombre}" creado exitosamente.`, id: data.id, nombre: data.nombre });
}

// ═══════════════════════════════════════════════════════════
// 14. ACTUALIZAR PRECIO DE PRODUCTO
// ═══════════════════════════════════════════════════════════
export async function actualizar_precio_producto({ nombre, nuevo_precio }) {
  if (!nombre || !nuevo_precio) return "Necesito el nombre del producto y el nuevo precio.";
  const productos = await busquedaInteligente("productos", "nombre", nombre, "id, nombre, precio", 5);
  if (productos.length === 0) {
    const similares = await articulosParecidos(nombre);
    if (similares.length > 0) {
      return `No encontré el producto "${nombre}". Estos se parecen:\n${listaParecidos(similares)}\n\nIMPORTANTE: pregúntale al usuario cuál es antes de cambiar ningún precio. NUNCA actualices un precio sin confirmación explícita.`;
    }
    return `No se encontró el producto "${nombre}" ni nada parecido en el inventario.`;
  }
  if (productos.length > 1) return JSON.stringify({ exito: false, mensaje: `Encontré ${productos.length} productos similares. ¿Cuál quieres actualizar?`, productos: productos.map((p) => ({ nombre: p.nombre, precio_actual: money(p.precio) })) });
  const prod = productos[0]; const precioAnterior = Number(prod.precio || 0);
  const { error } = await supabase.from("productos").update({ precio: Number(nuevo_precio) }).eq("id", prod.id);
  if (error) { console.error("Error actualizando precio:", error); return "Error al actualizar el precio."; }
  return JSON.stringify({ exito: true, mensaje: `✅ Precio de "${prod.nombre}" actualizado.`, producto: prod.nombre, precio_anterior: money(precioAnterior), precio_nuevo: money(nuevo_precio) });
}

// ═══════════════════════════════════════════════════════════
// 15. ARTÍCULOS MÁS ALQUILADOS
// ═══════════════════════════════════════════════════════════
export async function articulos_mas_alquilados({ limite }) {
  const { data: ordenes } = await supabase.from("ordenes_pedido").select("productos").limit(200);
  if (!ordenes || ordenes.length === 0) return "No hay pedidos para analizar.";
  const conteo = {};
  for (const orden of ordenes) { const walk = (list, factor = 1) => { for (const it of list || []) { if (it.es_grupo && Array.isArray(it.productos)) { walk(it.productos, factor * (Number(it.cantidad) || 1)); } else if (it.nombre && !it.es_servicio) { const key = it.nombre; if (!conteo[key]) conteo[key] = { nombre: key, veces: 0, unidades: 0 }; conteo[key].veces += 1; conteo[key].unidades += (Number(it.cantidad) || 0) * factor; } } }; walk(orden.productos); }
  const ranking = Object.values(conteo).sort((a, b) => b.unidades - a.unidades).slice(0, limite || 10);
  return JSON.stringify({ total_pedidos_analizados: ordenes.length, ranking: ranking.map((r, i) => ({ posicion: i + 1, nombre: r.nombre, veces_alquilado: r.veces, unidades_totales: r.unidades })) });
}

// ═══════════════════════════════════════════════════════════
// 16. CLIENTES MÁS FRECUENTES
// ═══════════════════════════════════════════════════════════
export async function clientes_mas_frecuentes({ limite }) {
  const { data: ordenes } = await supabase.from("ordenes_pedido").select("total_neto, clientes(nombre)").order("fecha_evento", { ascending: false }).limit(500);
  if (!ordenes || ordenes.length === 0) return "No hay pedidos para analizar.";
  const conteo = {};
  for (const o of ordenes) { const nom = o.clientes?.nombre || "Desconocido"; if (!conteo[nom]) conteo[nom] = { nombre: nom, pedidos: 0, total: 0 }; conteo[nom].pedidos += 1; conteo[nom].total += Number(o.total_neto || 0); }
  const ranking = Object.values(conteo).sort((a, b) => b.pedidos - a.pedidos).slice(0, limite || 10);
  return JSON.stringify({ ranking: ranking.map((r, i) => ({ posicion: i + 1, nombre: r.nombre, total_pedidos: r.pedidos, facturacion: money(r.total) })) });
}

// ═══════════════════════════════════════════════════════════
// 17. BUSCAR PAQUETE DE EVENTOS
// ═══════════════════════════════════════════════════════════
export async function buscar_paquete({ nombre }) {
  const texto = nombre || "";
  let paquetes;

  if (texto) {
    paquetes = await busquedaInteligente("paquetes_eventos", "nombre", texto, "id, nombre, descripcion, productos, precio_total, created_at", 5);
  }

  if ((!paquetes || paquetes.length === 0) && !texto) {
    const { data: todos } = await supabase.from("paquetes_eventos").select("id, nombre, descripcion, productos, precio_total, created_at").order("nombre").limit(10);
    paquetes = todos;
  }

  if (!paquetes || paquetes.length === 0) {
    return texto ? `No encontré paquetes que coincidan con "${texto}".` : "No hay paquetes de eventos creados todavía.";
  }

  const resultados = paquetes.map((p) => {
    const items = p.productos || [];
    const contenido = [];
    for (const item of items) {
      if (item.es_grupo && Array.isArray(item.productos)) {
        const sub = item.productos.map((s) => `  • ${s.cantidad || 0}x ${s.nombre}`).join("\n");
        contenido.push(`📦 Grupo: ${item.nombre} (x${item.cantidad || 1})\n${sub}`);
      } else {
        const emoji = item.es_servicio ? "🔧" : item.es_proveedor ? "🏢" : "📦";
        contenido.push(`${emoji} ${item.cantidad || 0}x ${item.nombre} — ${money(item.precio)}`);
      }
    }
    const totalItems = items.reduce((acc, it) => {
      if (it.es_grupo && Array.isArray(it.productos)) return acc + it.productos.length;
      return acc + 1;
    }, 0);
    return { nombre: p.nombre, descripcion: p.descripcion || "", total_items: totalItems, precio_total: money(p.precio_total), contenido: contenido.join("\n") };
  });
  return JSON.stringify(resultados);
}

// ═══════════════════════════════════════════════════════════
// 18. CREAR CITA
// ═══════════════════════════════════════════════════════════
export async function crear_cita({ cliente, fecha, hora, motivo }) {
  if (!cliente) return "Necesito el nombre del cliente para agendar la cita.";
  if (!fecha) return "Necesito la fecha de la cita (formato AAAA-MM-DD).";

  // Buscar cliente
  const clienteData = await busquedaInteligente("clientes", "nombre", cliente, "id, nombre, telefono", 10);
  if (clienteData.length === 0) return `No encontré ningún cliente con el nombre "${cliente}". Para registrarlo y agendar la cita necesito sus datos: nombre completo, teléfono, y si tienes: cédula, email o dirección. Dime los datos y me encargo.`;

  // Verificar si el match es exacto (nombre completo idéntico)
  const nombreBuscado = sinTildes(cliente.trim().toLowerCase());
  const matchExacto = clienteData.find(c => sinTildes(c.nombre.trim().toLowerCase()) === nombreBuscado);

  if (!matchExacto) {
    // No hay match exacto — mostrar lista y pedir confirmación
    const lista = clienteData.slice(0, 10).map(c =>
      `• ${c.nombre}${c.telefono ? " — Tel: " + c.telefono : ""}`
    ).join("\n");
    return `No encontré un cliente exacto con "${cliente}", pero estos se parecen:\n\n${lista}\n\n¿Es alguno de estos? Si no, puedo crear el cliente nuevo y agendar la cita.`;
  }

  const cl = matchExacto;
  const horaFinal = hora || "09:00";

  const { data, error } = await supabase.from("citas").insert([{
    cliente_id: cl.id, fecha, hora: horaFinal, estado: "programada", motivo: motivo || "Cita agendada", notas: ""
  }]).select("id").single();

  if (error) { console.error("Error creando cita:", error); return "Error al agendar la cita."; }
  return JSON.stringify({ exito: true, mensaje: `✅ Cita agendada con ${cl.nombre} el ${fecha} a las ${horaFinal}`, id: data.id, cliente: cl.nombre, telefono: cl.telefono || "—", fecha, hora: horaFinal, motivo: motivo || "Cita agendada" });
}

// ═══════════════════════════════════════════════════════════
// 19. CONSULTAR CITAS
// ═══════════════════════════════════════════════════════════
export async function consultar_citas({ fecha, periodo, cliente, estado }) {
  let desde, hasta;

  if (fecha) { desde = hasta = fecha; }
  else if (periodo) {
    const hoy = new Date(); desde = hoyISO();
    if (periodo === "hoy") { hasta = desde; }
    else if (periodo === "manana") { const m = new Date(hoy); m.setDate(m.getDate() + 1); desde = hasta = fechaLocal(m); }
    else if (periodo === "semana") { const f = new Date(hoy); f.setDate(f.getDate() + 7); hasta = fechaLocal(f); }
    else if (periodo === "mes") { const f = new Date(hoy); f.setDate(f.getDate() + 30); hasta = fechaLocal(f); }
    else { hasta = desde; }
  } else {
    // Por defecto: hoy y los próximos 7 días
    const hoy = new Date(); desde = hoyISO();
    const f = new Date(hoy); f.setDate(f.getDate() + 7); hasta = fechaLocal(f);
  }

  let query = supabase.from("citas").select("id, fecha, hora, motivo, estado, notas, clientes(nombre, telefono)")
    .gte("fecha", desde).lte("fecha", hasta).order("fecha").order("hora").limit(20);

  if (estado) query = query.eq("estado", estado);

  const { data, error } = await query;
  if (error || !data || data.length === 0) {
    return cliente
      ? `No hay citas con "${cliente}" en ese período.`
      : `No hay citas programadas del ${desde} al ${hasta}.`;
  }

  let resultado = data;
  if (cliente) {
    const cn = sinTildes(cliente.toLowerCase());
    resultado = resultado.filter(c => {
      const nom = (c.clientes?.nombre || "").toLowerCase();
      return nom.includes(cliente.toLowerCase()) || sinTildes(nom).includes(cn);
    });
    if (resultado.length === 0) return `No hay citas con "${cliente}" en ese período.`;
  }

  return JSON.stringify({
    total: resultado.length,
    citas: resultado.map(c => ({
      id: c.id, fecha: c.fecha, hora: (c.hora || "").slice(0, 5),
      cliente: c.clientes?.nombre || "—", telefono: c.clientes?.telefono || "—",
      motivo: c.motivo || "—", estado: c.estado, notas: c.notas || ""
    }))
  });
}

// ═══════════════════════════════════════════════════════════
// 20. CAMBIAR ESTADO DE CITA
// ═══════════════════════════════════════════════════════════
export async function cambiar_estado_cita({ cliente, fecha, nuevo_estado }) {
  const estadosValidos = ["programada", "confirmada", "completada", "no_asistio", "cancelada"];
  if (!nuevo_estado || !estadosValidos.includes(nuevo_estado)) {
    return `Estado no válido. Opciones: ${estadosValidos.join(", ")}`;
  }

  let query = supabase.from("citas").select("id, fecha, hora, estado, clientes(nombre)").eq("estado", "programada");
  if (fecha) query = query.eq("fecha", fecha);

  const { data: citas } = await query.limit(10);
  if (!citas || citas.length === 0) return "No encontré citas activas para modificar.";

  let citaTarget = citas;
  if (cliente) {
    const cn = sinTildes(cliente.toLowerCase());
    citaTarget = citas.filter(c => {
      const nom = (c.clientes?.nombre || "").toLowerCase();
      return nom.includes(cliente.toLowerCase()) || sinTildes(nom).includes(cn);
    });
  }

  if (citaTarget.length === 0) return `No encontré citas de "${cliente}" para modificar.`;
  if (citaTarget.length > 1) return `Encontré ${citaTarget.length} citas. ¿Puedes especificar la fecha para identificar cuál?`;

  const cita = citaTarget[0];
  const { error } = await supabase.from("citas").update({ estado: nuevo_estado }).eq("id", cita.id);
  if (error) return "Error al actualizar el estado de la cita.";

  const labels = { programada: "Programada", confirmada: "Confirmada", completada: "Completada", no_asistio: "No asistió", cancelada: "Cancelada" };
  return JSON.stringify({ exito: true, mensaje: `✅ Cita con ${cita.clientes?.nombre || "cliente"} actualizada a "${labels[nuevo_estado]}"`, id: cita.id, nuevo_estado });
}

// ═══════════════════════════════════════════════════════════
// 21. CONSULTAR NOTIFICACIONES (citas de hoy y mañana)
// ═══════════════════════════════════════════════════════════
export async function consultar_notificaciones() {
  const hoy = new Date();
  const manana = new Date(hoy); manana.setDate(manana.getDate() + 1);
  const hoyStr = fechaLocal(hoy);
  const mananaStr = fechaLocal(manana);

  const { data: citas } = await supabase.from("citas")
    .select("id, fecha, hora, motivo, estado, clientes(nombre, telefono)")
    .in("fecha", [hoyStr, mananaStr])
    .in("estado", ["programada", "confirmada"])
    .order("fecha").order("hora");

  if (!citas || citas.length === 0) return "No hay notificaciones pendientes. No tienes citas para hoy ni mañana.";

  const citasHoy = citas.filter(c => c.fecha === hoyStr);
  const citasManana = citas.filter(c => c.fecha === mananaStr);

  return JSON.stringify({
    total: citas.length,
    hoy: citasHoy.map(c => ({ hora: (c.hora || "").slice(0, 5), cliente: c.clientes?.nombre || "—", motivo: c.motivo || "—", estado: c.estado })),
    manana: citasManana.map(c => ({ hora: (c.hora || "").slice(0, 5), cliente: c.clientes?.nombre || "—", motivo: c.motivo || "—", estado: c.estado })),
    total_hoy: citasHoy.length,
    total_manana: citasManana.length,
  });
}

// ═══════════════════════════════════════════════════════════
// 22. COMPARATIVA FINANCIERA (dos meses)
// ═══════════════════════════════════════════════════════════
export async function comparativa_financiera({ mes1, anio1, mes2, anio2 }) {
  const ahora = new Date();
  const m1 = mes1 || ahora.getMonth(); // mes anterior por defecto
  const a1 = anio1 || (m1 === 0 ? ahora.getFullYear() - 1 : ahora.getFullYear());
  const m2 = mes2 || (ahora.getMonth() + 1); // mes actual por defecto
  const a2 = anio2 || ahora.getFullYear();

  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  async function datosMes(mes, anio) {
    const desde = `${anio}-${String(mes).padStart(2, "0")}-01`;
    const ultimoDia = new Date(anio, mes, 0).getDate();
    const hasta = `${anio}-${String(mes).padStart(2, "0")}-${String(ultimoDia).padStart(2, "0")}`;
    const { data } = await supabase.from("movimientos_contables").select("tipo, monto, categoria").gte("fecha", desde).lte("fecha", hasta).neq("estado", "eliminado");
    const movs = data || [];
    const ingresos = movs.filter(m => m.tipo === "ingreso").reduce((s, m) => s + Number(m.monto || 0), 0);
    const gastos = movs.filter(m => m.tipo === "gasto").reduce((s, m) => s + Number(m.monto || 0), 0);
    return { periodo: `${meses[mes - 1]} ${anio}`, ingresos, gastos, ganancia: ingresos - gastos, movimientos: movs.length };
  }

  const [p1, p2] = await Promise.all([datosMes(m1, a1), datosMes(m2, a2)]);
  const cambioIng = p1.ingresos > 0 ? Math.round(((p2.ingresos - p1.ingresos) / p1.ingresos) * 100) : 0;
  const cambioGan = p1.ganancia > 0 ? Math.round(((p2.ganancia - p1.ganancia) / p1.ganancia) * 100) : 0;

  return JSON.stringify({
    periodo_1: { ...p1, ingresos: money(p1.ingresos), gastos: money(p1.gastos), ganancia: money(p1.ganancia) },
    periodo_2: { ...p2, ingresos: money(p2.ingresos), gastos: money(p2.gastos), ganancia: money(p2.ganancia) },
    variacion_ingresos: `${cambioIng >= 0 ? "+" : ""}${cambioIng}%`,
    variacion_ganancia: `${cambioGan >= 0 ? "+" : ""}${cambioGan}%`,
    tendencia: cambioGan > 0 ? "📈 Mejorando" : cambioGan < 0 ? "📉 Bajando" : "➡️ Estable",
  });
}

// ═══════════════════════════════════════════════════════════
// 23. FICHA CLIENTE 360° (CRM)
// ═══════════════════════════════════════════════════════════
export async function ficha_cliente({ cliente }) {
  if (!cliente || !cliente.trim()) return "Necesito el nombre del cliente para consultar su ficha.";

  // Buscar cliente
  const clientes = await busquedaInteligente("clientes", "nombre", cliente, "id, nombre, telefono, email, identificacion, direccion", 3);
  if (clientes.length === 0) return `No encontré clientes que coincidan con "${cliente}".`;
  const c = clientes[0];

  // Cargar pedidos, cotizaciones y citas en paralelo
  const [{ data: pedidos }, { data: cotizaciones }, { data: citas }] = await Promise.all([
    supabase.from("ordenes_pedido").select("id, numero, fecha_evento, total_neto, abonos, estado, productos, tipo_evento").eq("cliente_id", c.id).order("fecha_evento", { ascending: false }).limit(50),
    supabase.from("cotizaciones").select("id, numero, fecha_evento, total_neto, tipo_evento").eq("cliente_id", c.id).order("fecha_evento", { ascending: false }).limit(50),
    supabase.from("citas").select("id, fecha, hora, motivo, estado").eq("cliente_id", c.id).order("fecha", { ascending: false }).limit(10),
  ]);

  const ords = pedidos || [];
  const cots = cotizaciones || [];
  const citasArr = citas || [];

  // KPIs
  const recaudoTotal = ords.reduce((s, o) => s + Number(o.total_neto || 0), 0);
  const totalPedidos = ords.length;
  const ticketPromedio = totalPedidos > 0 ? Math.round(recaudoTotal / totalPedidos) : 0;

  // Días sin pedir
  const ultimoPedido = ords.length > 0 ? ords[0].fecha_evento : null;
  const diasSinPedir = ultimoPedido
    ? Math.floor((new Date() - new Date(ultimoPedido)) / (1000 * 60 * 60 * 24))
    : null;

  // Conversión cotizaciones → pedidos
  const conversion = cots.length > 0
    ? `${Math.round((totalPedidos / (totalPedidos + cots.length)) * 100)}%`
    : "N/A";

  // Segmento RFM
  let segmento = "Sin pedidos";
  if (totalPedidos > 0 && diasSinPedir !== null) {
    if (totalPedidos >= 4 && diasSinPedir < 90) segmento = "VIP";
    else if (totalPedidos >= 3 && diasSinPedir < 180) segmento = "Frecuente";
    else if (totalPedidos === 1 && diasSinPedir < 90) segmento = "Nuevo";
    else if (totalPedidos >= 2 && diasSinPedir >= 90 && diasSinPedir <= 270) segmento = "En Riesgo";
    else if (diasSinPedir > 270) segmento = "Dormido";
    else segmento = "Ocasional";
  }

  // Productos favoritos (top 5)
  const productoFrec = {};
  for (const o of ords) {
    const walk = (list) => {
      for (const it of list) {
        if (it.es_grupo && Array.isArray(it.productos)) walk(it.productos);
        else if (it.nombre) productoFrec[it.nombre] = (productoFrec[it.nombre] || 0) + (Number(it.cantidad) || 1);
      }
    };
    walk(o.productos || []);
  }
  const productosFav = Object.entries(productoFrec)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([nombre, veces]) => `${nombre} (${veces}x)`);

  // Tipos de evento
  const tiposEvento = [...new Set(ords.map(o => o.tipo_evento).filter(Boolean))];

  // Saldo pendiente
  let saldoPendiente = 0;
  for (const o of ords) {
    const total = Number(o.total_neto || 0);
    const abonado = (o.abonos || []).reduce((s, a) => s + Number(a.valor || a || 0), 0);
    saldoPendiente += Math.max(0, total - abonado);
  }

  // Últimos 5 pedidos
  const ultimosPedidos = ords.slice(0, 5).map(o => ({
    numero: o.numero || "—",
    fecha: o.fecha_evento,
    total: money(o.total_neto),
    estado: o.estado || "pendiente",
    tipo_evento: o.tipo_evento || ""
  }));

  return JSON.stringify({
    cliente: { nombre: c.nombre, telefono: c.telefono || "—", email: c.email || "—", identificacion: c.identificacion || "—", direccion: c.direccion || "—" },
    segmento,
    kpis: {
      recaudo_total: money(recaudoTotal),
      total_pedidos: totalPedidos,
      total_cotizaciones: cots.length,
      ticket_promedio: money(ticketPromedio),
      dias_sin_pedir: diasSinPedir !== null ? `${diasSinPedir} días` : "Nunca ha pedido",
      conversion,
      saldo_pendiente: saldoPendiente > 0 ? money(saldoPendiente) : "$0",
    },
    productos_favoritos: productosFav.length > 0 ? productosFav : ["Sin historial"],
    tipos_evento: tiposEvento.length > 0 ? tiposEvento : ["Sin datos"],
    ultimos_pedidos: ultimosPedidos,
    citas_recientes: citasArr.slice(0, 3).map(ci => ({
      fecha: ci.fecha, hora: (ci.hora || "").slice(0, 5), motivo: ci.motivo || "—", estado: ci.estado
    })),
  });
}

// ═══════════════════════════════════════════════════════════
// 24. ALERTAS CRM
// ═══════════════════════════════════════════════════════════
export async function consultar_alertas_crm() {
  const hoy = hoyISO();
  const hace5Dias = new Date(); hace5Dias.setDate(hace5Dias.getDate() - 5);
  const hace5Str = fechaLocal(hace5Dias);
  const hace9Meses = new Date(); hace9Meses.setMonth(hace9Meses.getMonth() - 9);
  const hace9Str = fechaLocal(hace9Meses);

  // 1. Cotizaciones pendientes (>5 días sin convertir)
  const { data: cotsPendientes } = await supabase.from("cotizaciones")
    .select("id, numero, fecha_evento, total_neto, clientes(nombre)")
    .lte("created_at", hace5Str)
    .order("created_at", { ascending: false }).limit(10);

  // Filtrar las que no tienen pedido asociado (simplificado: cotizaciones viejas sin convertir)
  const cotizacionesPendientes = (cotsPendientes || []).map(c => ({
    numero: c.numero || "—", cliente: c.clientes?.nombre || "—",
    fecha_evento: c.fecha_evento, total: money(c.total_neto)
  }));

  // 2. Pedidos con saldo pendiente
  const { data: ordenes } = await supabase.from("ordenes_pedido")
    .select("id, numero, total_neto, abonos, clientes(nombre)")
    .eq("cerrada", false).limit(100);
  const pedidosPendientes = (ordenes || [])
    .map(o => {
      const total = Number(o.total_neto || 0);
      const abonado = (o.abonos || []).reduce((s, a) => s + Number(a.valor || a || 0), 0);
      return { numero: o.numero || "—", cliente: o.clientes?.nombre || "—", saldo: Math.max(0, total - abonado) };
    })
    .filter(o => o.saldo > 0);
  const totalSaldoPendiente = pedidosPendientes.reduce((s, o) => s + o.saldo, 0);

  // 3. Clientes dormidos (>9 meses sin pedir)
  const { data: todosClientes } = await supabase.from("clientes").select("id, nombre").limit(500);
  const { data: pedidosRecientes } = await supabase.from("ordenes_pedido")
    .select("cliente_id, fecha_evento")
    .gte("fecha_evento", hace9Str);

  const clientesActivos = new Set((pedidosRecientes || []).map(p => p.cliente_id));
  const clientesDormidos = (todosClientes || []).filter(c => !clientesActivos.has(c.id));

  return JSON.stringify({
    cotizaciones_sin_convertir: { total: cotizacionesPendientes.length, detalle: cotizacionesPendientes.slice(0, 5) },
    pedidos_pendientes_cobro: { total: pedidosPendientes.length, saldo_total: money(totalSaldoPendiente), detalle: pedidosPendientes.slice(0, 5).map(p => ({ ...p, saldo: money(p.saldo) })) },
    clientes_dormidos: { total: clientesDormidos.length, detalle: clientesDormidos.slice(0, 10).map(c => c.nombre) },
    resumen: `Tienes ${cotizacionesPendientes.length} cotización(es) pendientes, ${pedidosPendientes.length} pedido(s) por cobrar (${money(totalSaldoPendiente)}) y ${clientesDormidos.length} cliente(s) dormido(s).`
  });
}
// ═══════════════════════════════════════════════════════════
// 25. LISTAR CLIENTES POR SEGMENTO RFM
// ═══════════════════════════════════════════════════════════
export async function listar_clientes_segmento({ segmento }) {
  const segBuscado = (segmento || "todos").toLowerCase().trim();
  const segMap = { vip: "VIP", frecuente: "Frecuente", nuevo: "Nuevo", "en riesgo": "En Riesgo", dormido: "Dormido", ocasional: "Ocasional", "sin pedidos": "Sin pedidos" };

  // Traer todos los clientes y sus pedidos
  const { data: clientes } = await supabase.from("clientes").select("id, nombre, telefono").limit(500);
  if (!clientes || clientes.length === 0) return "No hay clientes registrados.";

  const { data: ordenes } = await supabase.from("ordenes_pedido").select("cliente_id, fecha_evento, total_neto").order("fecha_evento", { ascending: false }).limit(2000);
  const ordenesArr = ordenes || [];

  // Agrupar pedidos por cliente
  const pedidosPorCliente = {};
  for (const o of ordenesArr) {
    if (!pedidosPorCliente[o.cliente_id]) pedidosPorCliente[o.cliente_id] = [];
    pedidosPorCliente[o.cliente_id].push(o);
  }

  // Calcular segmento RFM de cada cliente
  const hoy = new Date();
  const resultado = clientes.map(c => {
    const peds = pedidosPorCliente[c.id] || [];
    const totalPedidos = peds.length;
    const recaudo = peds.reduce((s, p) => s + Number(p.total_neto || 0), 0);
    const ultimoPedido = peds.length > 0 ? peds[0].fecha_evento : null;
    const diasSinPedir = ultimoPedido ? Math.floor((hoy - new Date(ultimoPedido)) / (1000 * 60 * 60 * 24)) : null;

    let seg = "Sin pedidos";
    if (totalPedidos > 0 && diasSinPedir !== null) {
      if (totalPedidos >= 4 && diasSinPedir < 90) seg = "VIP";
      else if (totalPedidos >= 3 && diasSinPedir < 180) seg = "Frecuente";
      else if (totalPedidos === 1 && diasSinPedir < 90) seg = "Nuevo";
      else if (totalPedidos >= 2 && diasSinPedir >= 90 && diasSinPedir <= 270) seg = "En Riesgo";
      else if (diasSinPedir > 270) seg = "Dormido";
      else seg = "Ocasional";
    }

    return { nombre: c.nombre, telefono: c.telefono || "—", segmento: seg, total_pedidos: totalPedidos, recaudo: recaudo, dias_sin_pedir: diasSinPedir };
  });

  // Filtrar por segmento pedido
  let filtrados = resultado;
  if (segBuscado !== "todos") {
    const segNorm = segMap[segBuscado] || segBuscado;
    filtrados = resultado.filter(c => c.segmento === segNorm);
  }

  if (filtrados.length === 0) return `No hay clientes en el segmento "${segmento}".`;

  // Ordenar por recaudo descendente y limitar
  filtrados.sort((a, b) => b.recaudo - a.recaudo);
  const mostrar = filtrados.slice(0, 15);

  // Resumen por segmento (si pidieron todos)
  let resumenSegmentos = null;
  if (segBuscado === "todos") {
    const conteo = {};
    for (const c of resultado) { conteo[c.segmento] = (conteo[c.segmento] || 0) + 1; }
    resumenSegmentos = conteo;
  }

  return JSON.stringify({
    segmento_consultado: segBuscado === "todos" ? "Todos" : (segMap[segBuscado] || segmento),
    total_encontrados: filtrados.length,
    mostrando: mostrar.length,
    resumen_segmentos: resumenSegmentos,
    clientes: mostrar.map(c => ({
      nombre: c.nombre,
      telefono: c.telefono,
      segmento: c.segmento,
      total_pedidos: c.total_pedidos,
      recaudo_total: money(c.recaudo),
      dias_sin_pedir: c.dias_sin_pedir !== null ? `${c.dias_sin_pedir} días` : "Nunca"
    }))
  });
}

// ═══════════════════════════════════════════════════════════
// 26. CONSULTAR USO/RESERVAS DE UN ARTÍCULO
// ═══════════════════════════════════════════════════════════
export async function consultar_uso_articulo({ articulo, fecha_desde, fecha_hasta, modo }) {
  if (!articulo) return "Necesito el nombre del artículo para consultar.";

  // Buscar el artículo en inventario
  const productos = await busquedaInteligente("productos", "nombre", articulo, "id, nombre, stock, tipo", 3);
  if (productos.length === 0) return `No encontré artículos que coincidan con "${articulo}".`;
  const prod = productos[0];

  // Determinar modo automáticamente si no se especifica
  const hoy = hoyISO();
  let modoFinal = modo;
  if (!modoFinal) {
    if (fecha_desde && fecha_desde > hoy) modoFinal = "reservado";
    else if (fecha_hasta && fecha_hasta < hoy) modoFinal = "historico";
    else if (fecha_desde && fecha_hasta) modoFinal = fecha_hasta >= hoy ? "reservado" : "historico";
    else modoFinal = "historico";
  }

  // Definir rango de búsqueda
  let desdeBuscar, hastaBuscar;
  if (modoFinal === "reservado") {
    desdeBuscar = fecha_desde || hoy;
    hastaBuscar = fecha_hasta || fecha_desde || hoy;
  } else {
    if (fecha_desde) desdeBuscar = fecha_desde;
    else { const d = new Date(); d.setMonth(d.getMonth() - 3); desdeBuscar = fechaLocal(d); }
    hastaBuscar = fecha_hasta || hoy;
  }

  // Query amplio: traer por fecha_evento en rango extendido (±15 días)
  // para cubrir pedidos cuya fecha_entrega/devolucion solape pero fecha_evento esté fuera
  const extDesde = new Date(desdeBuscar); extDesde.setDate(extDesde.getDate() - 15);
  const extHasta = new Date(hastaBuscar); extHasta.setDate(extHasta.getDate() + 15);
  const { data: ordenesBruto } = await supabase.from("ordenes_pedido")
    .select("id, numero, fecha_evento, fecha_entrega, fecha_devolucion, productos, clientes(nombre)")
    .gte("fecha_evento", fechaLocal(extDesde))
    .lte("fecha_evento", fechaLocal(extHasta))
    .order("fecha_evento", { ascending: true })
    .limit(300);

  // Filtrar en JS para máxima fiabilidad (cubre nulls en fecha_entrega/fecha_devolucion)
  const ordenes = (ordenesBruto || []).filter(o => {
    const entrega = o.fecha_entrega || o.fecha_evento;
    const devolucion = o.fecha_devolucion || o.fecha_evento;
    const evento = o.fecha_evento;
    if (modoFinal === "reservado") {
      // Solapamiento: entrega <= hasta Y devolucion >= desde
      return entrega <= hastaBuscar && devolucion >= desdeBuscar;
    } else {
      // Histórico: fecha_evento dentro del rango
      return evento >= desdeBuscar && evento <= hastaBuscar;
    }
  });

  if (ordenes.length === 0) return `No encontré pedidos ${modoFinal === "reservado" ? "con reservas en esas fechas" : "en el período"} para "${prod.nombre}".`;

  // Buscar el artículo dentro de los productos de cada orden
  const nombreBuscado = sinTildes(prod.nombre.toLowerCase());
  const detalle = [];
  let cantidadTotal = 0;

  for (const orden of ordenes) {
    let cantidadEnOrden = 0;
    const walk = (list, factor = 1) => {
      for (const it of list || []) {
        if (it.es_grupo && Array.isArray(it.productos)) {
          walk(it.productos, factor * (Number(it.cantidad) || 1));
        } else {
          const nomItem = sinTildes((it.nombre || "").toLowerCase());
          if (it.id === prod.id || it.producto_id === prod.id || nomItem === nombreBuscado) {
            cantidadEnOrden += (Number(it.cantidad) || 0) * factor;
          }
        }
      }
    };
    walk(orden.productos || []);

    if (cantidadEnOrden > 0) {
      cantidadTotal += cantidadEnOrden;
      detalle.push({
        numero: orden.numero || "—",
        cliente: orden.clientes?.nombre || "—",
        fecha: modoFinal === "reservado"
          ? `${orden.fecha_entrega || orden.fecha_evento} → ${orden.fecha_devolucion || orden.fecha_evento}`
          : orden.fecha_evento,
        cantidad: cantidadEnOrden,
        _id: orden.id,
        _tipo: "pedido"
      });
    }
  }

  if (detalle.length === 0) return `"${prod.nombre}" no aparece en ningún pedido ${modoFinal === "reservado" ? "con reserva en esas fechas" : "del período consultado"}.`;

  const stockTotal = Number(prod.stock || 0);
  const resumen = modoFinal === "reservado"
    ? { disponible_neto: Math.max(0, stockTotal - cantidadTotal), stock_total: stockTotal, comprometido: cantidadTotal }
    : null;

  return JSON.stringify({
    articulo: prod.nombre,
    modo: modoFinal === "reservado" ? "Reservas futuras" : "Histórico",
    rango: `${desdeBuscar} → ${hastaBuscar}`,
    cantidad_total: cantidadTotal,
    ordenes_encontradas: detalle.length,
    resumen_stock: resumen,
    detalle: detalle.slice(0, 10),
    _acciones: detalle.slice(0, 3).map(d => ({ _id: d._id, _tipo: d._tipo, numero: d.numero }))
  });
}